/**
 * Password-setup landing for admin-provisioned businesses with custom offers.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Loader,
  Paper,
  PasswordInput,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PasswordRequirements, {
  isLocallyValidPassword,
} from "@/components/PasswordRequirements";
import { AppRoutes } from "@/constants";
import { useAuthStore } from "@/utils/stores/authStore";
import {
  completeCustomContractOfferInvite,
  fetchCustomContractOfferInviteStatus,
  type CustomContractOfferInviteStatus,
} from "./api";

const CustomContractOfferInvitePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const refreshAuth = useAuthStore((s) => s.refreshAuth);
  const [status, setStatus] = useState<CustomContractOfferInviteStatus | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    fetchCustomContractOfferInviteStatus(token).then((result) => {
      setStatus(result);
      setLoading(false);
    });
  }, [token]);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setError(null);
      if (!isLocallyValidPassword(password)) {
        setError("Password does not meet requirements.");
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
      setSubmitting(true);
      try {
        await completeCustomContractOfferInvite(token, password);
        await refreshAuth();
        navigate(AppRoutes.gettingStarted, { replace: true });
      } catch (e: unknown) {
        const msg =
          e &&
          typeof e === "object" &&
          "response" in e &&
          (e as { response?: { data?: { message?: string } } }).response?.data
            ?.message;
        setError(msg ? String(msg) : "Could not complete setup.");
      } finally {
        setSubmitting(false);
      }
    },
    [confirmPassword, navigate, password, refreshAuth, token]
  );

  if (loading) {
    return (
      <Box p="xl">
        <Loader />
      </Box>
    );
  }

  if (!token || !status) {
    return (
      <Box p="xl" maw={480} mx="auto">
        <Alert icon={<IconAlertCircle />} color="red" title="Invalid link">
          This invite link is invalid or expired. Contact SmartWiz support for a
          new link.
        </Alert>
      </Box>
    );
  }

  if (!status.mustSetPassword) {
    return (
      <Box p="xl" maw={480} mx="auto">
        <Alert color="blue" title="Already set up">
          Your account is ready.{" "}
          <Button variant="subtle" onClick={() => navigate(AppRoutes.login)}>
            Sign in
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box p="xl" maw={480} mx="auto">
      <Paper p="xl" radius="md" withBorder>
        <Stack gap="md">
          <Title order={3}>Set your password</Title>
          <Text size="sm" c="dimmed">
            Welcome to <strong>{status.businessName}</strong>. After you choose
            a password, you will review your locked contract terms in
            onboarding.
          </Text>
          <form onSubmit={handleSubmit}>
            <Stack gap="sm">
              <PasswordRequirements
                value={password}
                setValue={setPassword}
                placeholder="Password"
                name="password"
                variant="default"
                radius="md"
              />
              <PasswordInput
                label="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                required
              />
              {error ? (
                <Alert color="red" icon={<IconAlertCircle size={16} />}>
                  {error}
                </Alert>
              ) : null}
              <Button type="submit" loading={submitting}>
                Continue
              </Button>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Box>
  );
};

export default CustomContractOfferInvitePage;
